import { money, toMoney } from './money.js'

/**
 * Line and document totals for sales documents — one implementation for the web preview and the
 * authoritative API computation.
 *
 * PRESERVED from the legacy calculator (characterised in workflows/sales-ar-accounting.md §1):
 *   - every step is rounded to 3 dp; tax is computed and rounded per line, then summed;
 *   - discount types: PERCENTAGE of the line, PER_UNIT amount × quantity (legacy "AMOUNT"),
 *     LINE_AMOUNT flat per line (legacy "TOTAL"); the discount is capped at the line subtotal;
 *   - tax-inclusive prices: net = round(gross / (1 + rate)), tax = gross − net (the customer pays
 *     exactly the entered price).
 * CORRECTED (ASM-010): in tax-inclusive mode the revenue ("gross") and discount figures used for
 * posting are ex-VAT, so VAT never inflates revenue or the discount account. Net, tax and total are
 * identical to legacy.
 */

export type DiscountType = 'PERCENTAGE' | 'PER_UNIT' | 'LINE_AMOUNT'

export interface LineInput {
  quantity: string
  unitPrice: string
  discountType?: DiscountType
  discountValue?: string
  /** Percent, e.g. "5". */
  taxRate: string
}

export interface LineTotals {
  /** quantity × unit price, on the entered price basis. */
  lineSubtotal: string
  /** Discount on the entered price basis. */
  discount: string
  /** Taxable amount: ex-VAT, after discount. */
  net: string
  tax: string
  /** What the customer pays for the line. */
  total: string
  /** Ex-VAT amount before discount (revenue posting). */
  grossNet: string
  /** Ex-VAT discount (discount posting); grossNet − discountNet = net exactly. */
  discountNet: string
}

export interface DocumentTotals {
  /** Σ grossNet — ex-VAT revenue before discounts. */
  gross: string
  /** Σ discountNet — ex-VAT discounts. */
  discount: string
  /** Σ net — taxable amount. */
  subtotal: string
  tax: string
  total: string
  taxByRate: { rate: string; net: string; tax: string }[]
}

const r3 = (v: ReturnType<typeof money>) => money(toMoney(v))

export function calculateLine(line: LineInput, pricesIncludeTax: boolean): LineTotals {
  const qty = money(line.quantity)
  const price = money(line.unitPrice)
  const rate = money(line.taxRate)
  const value = money(line.discountValue ?? 0)
  const lineSubtotal = r3(qty.times(price))

  let discount: ReturnType<typeof money>
  switch (line.discountType ?? 'PER_UNIT') {
    case 'PERCENTAGE':
      discount = r3(lineSubtotal.times(value).dividedBy(100))
      break
    case 'LINE_AMOUNT':
      discount = r3(value)
      break
    default:
      discount = r3(value.times(qty))
  }
  if (!lineSubtotal.greaterThan(0)) discount = money(0)
  else if (discount.greaterThan(lineSubtotal)) discount = lineSubtotal
  if (discount.isNegative()) discount = money(0)

  const afterDiscount = r3(lineSubtotal.minus(discount))
  const divisor = money(1).plus(rate.dividedBy(100))

  if (pricesIncludeTax && rate.greaterThan(0)) {
    const net = r3(afterDiscount.dividedBy(divisor))
    const tax = r3(afterDiscount.minus(net))
    const grossNet = r3(lineSubtotal.dividedBy(divisor))
    return {
      lineSubtotal: toMoney(lineSubtotal),
      discount: toMoney(discount),
      net: toMoney(net),
      tax: toMoney(tax),
      total: toMoney(afterDiscount),
      grossNet: toMoney(grossNet),
      discountNet: toMoney(grossNet.minus(net)),
    }
  }
  const tax = r3(afterDiscount.times(rate).dividedBy(100))
  return {
    lineSubtotal: toMoney(lineSubtotal),
    discount: toMoney(discount),
    net: toMoney(afterDiscount),
    tax: toMoney(tax),
    total: toMoney(afterDiscount.plus(tax)),
    grossNet: toMoney(lineSubtotal),
    discountNet: toMoney(discount),
  }
}

export function calculateDocument(
  lines: LineInput[],
  pricesIncludeTax: boolean,
): {
  lines: LineTotals[]
  totals: DocumentTotals
} {
  const computed = lines.map((l) => calculateLine(l, pricesIncludeTax))
  const sum = (key: keyof LineTotals) => toMoney(computed.reduce((s, l) => s.plus(l[key]), money(0)))
  const byRate = new Map<string, { net: ReturnType<typeof money>; tax: ReturnType<typeof money> }>()
  computed.forEach((l, i) => {
    const rate = money(lines[i]?.taxRate ?? 0).toFixed(2)
    const acc = byRate.get(rate) ?? { net: money(0), tax: money(0) }
    byRate.set(rate, { net: acc.net.plus(l.net), tax: acc.tax.plus(l.tax) })
  })
  return {
    lines: computed,
    totals: {
      gross: sum('grossNet'),
      discount: sum('discountNet'),
      subtotal: sum('net'),
      tax: sum('tax'),
      total: sum('total'),
      taxByRate: [...byRate.entries()]
        .sort(([a], [b]) => Number(b) - Number(a))
        .map(([rate, v]) => ({ rate, net: toMoney(v.net), tax: toMoney(v.tax) })),
    },
  }
}
